import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ConsultationModal from "@/components/ConsultationModal";
import { ArrowRight, ArrowLeft, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaticPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/pages/${slug}`)
      .then(res => setPage(res.data))
      .catch(err => {
        setError(err.response?.status === 404 ? "Page not found" : "Failed to load page");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-bar" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="font-heading text-4xl font-semibold text-slate-900 mb-4">Page Not Found</h1>
        <p className="text-slate-500 mb-8">{error}</p>
        <Link to="/">
          <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="static-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-sm text-orange-600 hover:text-orange-700 mb-4">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to Home
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
            {page.title}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="prose prose-slate prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-slate-900
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-ul:text-slate-600 prose-ul:my-4
              prose-li:my-1
              prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: page.content }}
            data-testid="static-page-content"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-3">Need Assistance?</h2>
              <p className="text-slate-500 mb-6">Our team is here to help you with any questions or concerns</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <ConsultationModal
                  trigger={
                    <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 py-5 font-semibold" data-testid="static-page-cta-btn">
                      Contact Us <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  }
                />
                <a href="tel:+917289007800">
                  <Button variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 rounded-full px-6 py-5 font-semibold">
                    <Phone className="mr-2 h-4 w-4" /> +91 7289007800
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
